// 選択範囲の純ロジック。8bit マスク (0..255) を密な配列で扱う。
// ここは Canvas / WebGL に依存しない (Node テスト可能)。
//
// SelectionMask は「文書サイズの矩形」ではなく bounds 付きの密配列で持ち、
// PixelSurface (1ch) との変換は selectionSurface.ts が担う。

import type { Pt, Rect } from '@/types/geometry'
import { rectIntersect, rectUnion } from '@/types/geometry'

export interface MaskData {
  /** 文書座標での矩形 */
  rect: Rect
  /** rect.w * rect.h の 0..255 */
  data: Uint8ClampedArray
}

export type CombineMode = 'new' | 'add' | 'subtract' | 'intersect'

export function emptyMask(rect: Rect): MaskData {
  return { rect: { ...rect }, data: new Uint8ClampedArray(Math.max(0, rect.w * rect.h)) }
}

export function maskAt(m: MaskData, x: number, y: number): number {
  const lx = x - m.rect.x
  const ly = y - m.rect.y
  if (lx < 0 || ly < 0 || lx >= m.rect.w || ly >= m.rect.h) return 0
  return m.data[ly * m.rect.w + lx]!
}

/** 非ゼロ領域を覆う最小矩形。全部 0 なら null */
export function maskContentRect(m: MaskData): Rect | null {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
  const { w, h } = m.rect
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (m.data[y * w + x] === 0) continue
      if (x < x0) x0 = x
      if (y < y0) y0 = y
      if (x + 1 > x1) x1 = x + 1
      if (y + 1 > y1) y1 = y + 1
    }
  }
  if (x0 === Infinity) return null
  return { x: m.rect.x + x0, y: m.rect.y + y0, w: x1 - x0, h: y1 - y0 }
}

/** 非ゼロ領域だけに切り詰める (空なら null) */
export function trimMask(m: MaskData): MaskData | null {
  const r = maskContentRect(m)
  if (!r) return null
  if (r.x === m.rect.x && r.y === m.rect.y && r.w === m.rect.w && r.h === m.rect.h) return m
  return cropMask(m, r)
}

export function cropMask(m: MaskData, rect: Rect): MaskData {
  const out = emptyMask(rect)
  for (let y = 0; y < rect.h; y++) {
    for (let x = 0; x < rect.w; x++) out.data[y * rect.w + x] = maskAt(m, rect.x + x, rect.y + y)
  }
  return out
}

/** 2 つのマスクを合成する。結果は非ゼロ領域に切り詰める (空なら null) */
export function combineMasks(base: MaskData | null, next: MaskData, mode: CombineMode): MaskData | null {
  if (mode === 'new' || !base) return mode === 'subtract' ? base : trimMask(next)
  let rect: Rect
  if (mode === 'intersect') {
    const r = rectIntersect(base.rect, next.rect)
    if (!r) return null
    rect = r
  } else {
    rect = rectUnion(base.rect, next.rect)!
  }
  const out = emptyMask(rect)
  for (let y = 0; y < rect.h; y++) {
    for (let x = 0; x < rect.w; x++) {
      const a = maskAt(base, rect.x + x, rect.y + y)
      const b = maskAt(next, rect.x + x, rect.y + y)
      let v: number
      if (mode === 'add') v = Math.max(a, b)
      else if (mode === 'subtract') v = Math.min(a, 255 - b)
      else v = Math.min(a, b)
      out.data[y * rect.w + x] = v
    }
  }
  return trimMask(out)
}

/** 文書全体に対する反転 */
export function invertMask(m: MaskData | null, docW: number, docH: number): MaskData | null {
  const rect = { x: 0, y: 0, w: docW, h: docH }
  const out = emptyMask(rect)
  for (let y = 0; y < docH; y++) {
    for (let x = 0; x < docW; x++) out.data[y * docW + x] = 255 - (m ? maskAt(m, x, y) : 0)
  }
  return trimMask(out)
}

/** 矩形選択 (整数境界)。AA は端の部分被覆で表現 */
export function rasterizeRect(r: Rect, antialias = true): MaskData {
  const x0 = Math.floor(r.x), y0 = Math.floor(r.y)
  const x1 = Math.ceil(r.x + r.w), y1 = Math.ceil(r.y + r.h)
  const out = emptyMask({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 })
  for (let y = y0; y < y1; y++) {
    const cy = antialias ? Math.max(0, Math.min(y + 1, r.y + r.h) - Math.max(y, r.y)) : 1
    for (let x = x0; x < x1; x++) {
      const cx = antialias ? Math.max(0, Math.min(x + 1, r.x + r.w) - Math.max(x, r.x)) : 1
      out.data[(y - y0) * out.rect.w + (x - x0)] = Math.round(255 * cx * cy)
    }
  }
  return out
}

/** 楕円選択 (矩形に内接)。4x4 スーパーサンプリングで AA */
export function rasterizeEllipse(r: Rect, antialias = true): MaskData {
  const x0 = Math.floor(r.x), y0 = Math.floor(r.y)
  const x1 = Math.ceil(r.x + r.w), y1 = Math.ceil(r.y + r.h)
  const out = emptyMask({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 })
  const cx = r.x + r.w / 2, cy = r.y + r.h / 2
  const rx = r.w / 2, ry = r.h / 2
  if (rx <= 0 || ry <= 0) return out
  const S = antialias ? 4 : 1
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      let inside = 0
      for (let sy = 0; sy < S; sy++) {
        for (let sx = 0; sx < S; sx++) {
          const px = x + (sx + 0.5) / S, py = y + (sy + 0.5) / S
          const dx = (px - cx) / rx, dy = (py - cy) / ry
          if (dx * dx + dy * dy <= 1) inside++
        }
      }
      out.data[(y - y0) * out.rect.w + (x - x0)] = Math.round((255 * inside) / (S * S))
    }
  }
  return out
}

/** 多角形 (なげなわ・多角形選択)。偶奇規則、4x4 スーパーサンプリング */
export function rasterizePolygon(points: Pt[], antialias = true): MaskData | null {
  if (points.length < 3) return null
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const p of points) {
    if (p.x < minX) minX = p.x
    if (p.y < minY) minY = p.y
    if (p.x > maxX) maxX = p.x
    if (p.y > maxY) maxY = p.y
  }
  const x0 = Math.floor(minX), y0 = Math.floor(minY), x1 = Math.ceil(maxX), y1 = Math.ceil(maxY)
  if (x1 <= x0 || y1 <= y0) return null
  const out = emptyMask({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 })
  const S = antialias ? 4 : 1
  const n = points.length
  // スキャンラインごとに交点を求める
  for (let y = y0; y < y1; y++) {
    for (let sy = 0; sy < S; sy++) {
      const py = y + (sy + 0.5) / S
      const xs: number[] = []
      for (let i = 0; i < n; i++) {
        const a = points[i]!, b = points[(i + 1) % n]!
        if (a.y === b.y) continue
        if (py < Math.min(a.y, b.y) || py >= Math.max(a.y, b.y)) continue
        xs.push(a.x + ((py - a.y) * (b.x - a.x)) / (b.y - a.y))
      }
      xs.sort((p, q) => p - q)
      for (let k = 0; k + 1 < xs.length; k += 2) {
        const xa = xs[k]!, xb = xs[k + 1]!
        const cxa = Math.max(x0, Math.floor(xa)), cxb = Math.min(x1 - 1, Math.ceil(xb))
        for (let x = cxa; x <= cxb; x++) {
          // このピクセルのサブサンプル x のうち [xa, xb) に入る個数
          let cnt = 0
          for (let sx = 0; sx < S; sx++) {
            const px = x + (sx + 0.5) / S
            if (px >= xa && px < xb) cnt++
          }
          if (cnt) {
            const di = (y - y0) * out.rect.w + (x - x0)
            out.data[di] = out.data[di]! + Math.round((255 * cnt) / (S * S))
          }
        }
      }
    }
  }
  return trimMask(out)
}
